import React, { useCallback, useMemo } from 'react';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import { asRecord } from '../../utils';
import { TokenInput } from './tiptap/token-input';
import {
  buildUpstreamSources,
  type UpstreamSourceGroup,
} from './variable-picker-data';
import {
  FilterConditionsBuilder,
  type FilterConditionsConfig,
} from './filter-conditions-builder';
import type { WorkflowNodeEditorProps } from './types';

/**
 * Config editor for a filter node.
 *
 * The condition machinery is shared with the dataset trigger's "first matches
 * conditions" criterion ({@link FilterConditionsBuilder}); what is specific here
 * is the field picker, which offers JSON pointers into the immediate upstream
 * node's output.
 */
export const FilterNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  // Upstream fields for the "/"-command picker. Reuses the tool-call machinery,
  // but rewrites each ref to a BARE JSON pointer (the filter reads the single
  // immediate upstream), so inserting a field stores `{{/path}}`.
  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);
  // A filter can test its direct input OR any earlier node's output, so offer
  // every ancestor as a node-scoped `{{Node/field}}` reference — the same picker
  // (and sectioning) the tool nodes use. The runner resolves bare pointers
  // against the direct input and node-scoped refs against ancestor outputs.
  const fieldSources = useMemo<UpstreamSourceGroup[]>(
    () => buildUpstreamSources(upstreamSamples),
    [upstreamSamples]
  );

  const renderFieldInput = useCallback(
    ({
      value,
      onChange: onFieldChange,
    }: {
      value: string;
      onChange: (next: string) => void;
    }) => (
      <TokenInput
        value={value}
        sources={fieldSources}
        placeholder="Field (blank = whole value)"
        onChange={onFieldChange}
      />
    ),
    [fieldSources]
  );

  const handleChange = useCallback(
    (config: FilterConditionsConfig | null) => {
      onChange(config === null ? null : { type: 'filter', data: config });
    },
    [onChange]
  );

  return (
    <FilterConditionsBuilder
      initial={asRecord(node.data)}
      seedKey={node.id}
      onChange={handleChange}
      renderFieldInput={renderFieldInput}
      heading="Continue only if:"
    />
  );
};
